import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { BadgeComponent } from '@epicuro-next/ataraxia/badge';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { PopoverModule } from '@epicuro-next/ataraxia/popover';
import { dynamicElementsOrderStatusesKey } from '@epicuro-next/constants/dynamic-elements';
import { notAvailableLabel } from '@epicuro-next/constants/features/localization';
import { OrderStatus, OrderStatusType } from '@epicuro-next/models/order-book';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { BehaviorSubject } from 'rxjs';

type MaybeStatus = OrderStatus | undefined;

@DynamicComponentRegister(dynamicElementsOrderStatusesKey, true)
@Component({
  selector: 'epc-dynamic-elements-order-statuses',
  imports: [CommonModule, PopoverModule, IconsModule, BadgeComponent],
  templateUrl: './order-statuses.component.html',
  styleUrls: ['./order-statuses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicElementsStatusComponent {
  private titleCase = inject(TitleCasePipe);
  public statuses$ = new BehaviorSubject<OrderStatus[]>([]);

  @Input() public set statuses(statuses: MaybeStatus | MaybeStatus[]) {
    const statusValues = Array.isArray(statuses) ? statuses : [statuses];
    this.statuses$.next(
      statusValues.map((status) =>
        status
          ? this.titleCase.transform(status)
          : notAvailableLabel.toUpperCase(),
      ) as OrderStatus[],
    );
  }
  @Input() public showTooltips = false;

  public statusIcons = {
    [OrderStatusType.Failed]: ['fas', 'xmark'],
    [OrderStatusType.Pending]: ['fas', 'clock'],
    [OrderStatusType.Success]: ['fas', 'check'],
    [OrderStatusType.Warning]: ['fas', 'triangle-exclamation'],
  };

  public statusTooltipLocalize = {
    [OrderStatusType.Failed]: $localize`:OrderBook|Status tooltip text failed@@statusTooltipTextFailed:Cannot produce and/or deliver this item.`,
    [OrderStatusType.Pending]: $localize`:OrderBook|Status tooltip text pending@@statusTooltipTextPending:Your order has not been confirmed yet.`,
    [OrderStatusType.Success]: $localize`:OrderBook|Status tooltip text success@@statusTooltipTextSuccess:Your order will be delivered on the 'First Confirmed Date' (T&C apply)`,
    [OrderStatusType.Warning]: $localize`:OrderBook|Status tooltip text warning@@statusTooltipTextWarning:Your order has not beed delivered yet`,
  };

  public getStatusType(status: OrderStatus) {
    switch ((status ?? '').toLowerCase()) {
      case OrderStatus.Cancelled:
      case OrderStatus.FullyRejected:
        return OrderStatusType.Failed;

      case OrderStatus.NotDelivered:
        return OrderStatusType.Warning;

      case OrderStatus.Processing:
      case OrderStatus.Allocated:
      case OrderStatus.Reserved:
      case OrderStatus.Pending:
      case OrderStatus.PartiallyDelivered:
      case OrderStatus.PartiallyShipped:
        return OrderStatusType.Pending;

      case OrderStatus.Open:
        return OrderStatusType.New;

      case OrderStatus.Invoiced:
      case OrderStatus.Confirmed:
      case OrderStatus.Completed:
      case OrderStatus.Delivered:
      case OrderStatus.Shipped:
        return OrderStatusType.Success;

      default:
        return OrderStatusType.New;
    }
  }
}
